import * as yup from 'yup';
import { INfseDpsForm } from '../../../../../types/nfseEmissao';

const numero = () =>
  yup
    .number()
    .transform((v, original) => (original === '' || original === null ? undefined : v))
    .typeError('Informe um número');

const EXPORTACAO = '3';

const comCnpjNoBrasil = (doc?: string, exterior?: boolean) =>
  !exterior && (doc ?? '').replace(/\D/g, '').length === 14;
const textoObrigatorio = (mensagem: string) => (s: yup.StringSchema<any>) => s.required(mensagem);
const noExterior = (mensagem: string) =>
  yup
    .string()
    .nullable()
    .when('tomador_exterior', {
      is: true,
      then: textoObrigatorio(mensagem),
    });

export const dpsSchema = yup.object({
  emitente_id: yup.number().nullable().required('Selecione a empresa emitente'),
  competencia: yup.string().required('Informe a competência'),
  tomador_exterior: yup.boolean(),
  tomador_doc: yup.string().when('tomador_exterior', {
    is: true,
    otherwise: s =>
      s
        .required('Informe o CNPJ ou CPF do tomador')
        .test('doc', 'CNPJ ou CPF inválido', v =>
          [11, 14].includes((v ?? '').replace(/\D/g, '').length),
        ),
  }),
  // E0223: quem está no exterior vai com NIF ou com o motivo de não ter.
  tomador_nif: yup.string().nullable().max(40, 'Máximo de 40 caracteres'),
  tomador_c_nao_nif: yup
    .string()
    .nullable()
    .when(['tomador_exterior', 'tomador_nif'], {
      is: (exterior?: boolean, nif?: string) => !!exterior && !(nif ?? '').trim(),
      then: textoObrigatorio('Informe o NIF ou o motivo de não ter'),
    }),
  tomador_pais: noExterior('Informe o país do tomador'),
  tomador_cidade: noExterior('Informe a cidade do tomador').max(60, 'Máximo de 60 caracteres'),
  tomador_estado: noExterior('Informe o estado ou a província').max(60, 'Máximo de 60 caracteres'),
  tomador_cod_postal: noExterior('Informe o código postal').max(11, 'Máximo de 11 caracteres'),
  tomador_nome: yup.string().required('Informe o nome do tomador'),
  tomador_email: yup.string().email('E-mail inválido').nullable(),
  tomador_municipio_ibge: yup.string().nullable(),
  // Tomador com CNPJ precisa do endereço completo (regra E0235 da Sefin).
  tomador_cep: yup
    .string()
    .nullable()
    .when(['tomador_doc', 'tomador_exterior'], {
      is: comCnpjNoBrasil,
      then: textoObrigatorio('Informe o CEP do tomador'),
    }),
  tomador_logradouro: yup
    .string()
    .nullable()
    .when(['tomador_doc', 'tomador_exterior'], {
      is: (doc?: string, exterior?: boolean) => comCnpjNoBrasil(doc, exterior) || !!exterior,
      then: textoObrigatorio('Informe o logradouro do tomador'),
    }),
  tomador_bairro: yup
    .string()
    .nullable()
    .when(['tomador_doc', 'tomador_exterior'], {
      is: comCnpjNoBrasil,
      then: textoObrigatorio('Informe o bairro do tomador'),
    }),
  c_trib_nac: yup
    .string()
    .required('Informe o código de tributação nacional')
    .matches(/^\d{6}$/, 'O código tem 6 dígitos'),
  local_prestacao_exterior: yup.boolean(),
  local_prestacao_ibge: yup
    .string()
    .nullable()
    .when('local_prestacao_exterior', {
      is: true,
      otherwise: s =>
        s
          .required('Informe o município da prestação')
          .matches(/^\d{7}$/, 'O código IBGE tem 7 dígitos'),
    }),
  local_prestacao_pais: yup
    .string()
    .nullable()
    .when('local_prestacao_exterior', {
      is: true,
      then: textoObrigatorio('Informe o país da prestação'),
    }),
  // E0590: exportação prestada no Brasil informa onde o serviço deu resultado.
  pais_resultado: yup
    .string()
    .nullable()
    .when(['trib_issqn', 'local_prestacao_exterior'], {
      is: (trib?: string, exterior?: boolean) => trib === EXPORTACAO && !exterior,
      then: textoObrigatorio('Informe o país do resultado'),
    }),
  // E0330: operação com o exterior exige o grupo de comércio exterior.
  com_ext: yup.object().when(['tomador_exterior', 'local_prestacao_exterior', 'trib_issqn'], {
    is: (tomador?: boolean, local?: boolean, trib?: string) =>
      !!tomador || !!local || trib === EXPORTACAO,
    then: s =>
      s.shape({
        md_prestacao: yup.string().required('Escolha o modo de prestação'),
        tp_moeda: yup.string().required('Escolha a moeda'),
        v_serv_moeda: numero()
          .required('Informe o valor na moeda')
          .moreThan(0, 'O valor precisa ser maior que zero'),
        n_di: yup
          .string()
          .nullable()
          .max(12, 'Máximo de 12 caracteres')
          .when('mov_temp_bens', { is: '2', then: textoObrigatorio('Informe a DI') }),
        n_re: yup
          .string()
          .nullable()
          .max(12, 'Máximo de 12 caracteres')
          .when('mov_temp_bens', { is: '3', then: textoObrigatorio('Informe o RE') }),
      }),
  }),
  // Toda nota leva NBS, cIndOp e cClassTrib (a tela preenche pelo Anexo VIII).
  c_nbs: yup
    .string()
    .nullable()
    .test(
      'nbs',
      'Informe o código NBS (9 dígitos)',
      v => (v ?? '').replace(/\D/g, '').length === 9,
    ),
  c_ind_op: yup
    .string()
    .nullable()
    .test('indop', 'Escolha o indicador da operação', v => /^\d{6}$/.test(v ?? '')),
  c_class_trib: yup
    .string()
    .nullable()
    .test('classtrib', 'Escolha a classificação tributária', v => /^\d{6}$/.test(v ?? '')),
  descricao: yup.string().required('Descreva o serviço').max(2000, 'Máximo de 2.000 caracteres'),
  v_serv: numero()
    .required('Informe o valor do serviço')
    .moreThan(0, 'O valor precisa ser maior que zero'),
  trib_issqn: yup.string().required(),
  tp_ret_issqn: yup
    .string()
    .required()
    .when('trib_issqn', {
      is: EXPORTACAO,
      then: s => s.oneOf(['1'], 'Exportação não aceita retenção de ISS'),
    }),
  p_aliq: numero()
    .min(0)
    .max(5, 'A alíquota do ISS vai até 5%')
    .nullable()
    .when('trib_issqn', {
      is: EXPORTACAO,
      then: s => s.test('sem-aliquota', 'Exportação não aceita alíquota de ISS', v => v == null),
    }),
  p_tot_trib_fed: numero().min(0).max(100).nullable(),
  // RN18: dedução opcional, abaixo de 100% ou abaixo do valor do serviço.
  deducao_tipo: yup.string().oneOf(['valor', 'percentual']),
  deducao: numero()
    .nullable()
    .moreThan(0, 'A dedução precisa ser maior que zero')
    .when('deducao_tipo', {
      is: 'percentual',
      then: s => s.lessThan(100, 'A dedução precisa ficar abaixo de 100%'),
      otherwise: s =>
        s.test(
          'menor-que-servico',
          'A dedução precisa ser menor que o valor do serviço',
          function menor(v) {
            const servico = Number(this.parent.v_serv);
            return v === undefined || v === null || !servico || v < servico;
          },
        ),
    })
    .when('trib_issqn', {
      is: EXPORTACAO,
      then: s => s.test('sem-deducao', 'Exportação não aceita dedução do ISS', v => !v),
    }),
  enviar_email: yup.boolean(),
});

export const DPS_VAZIA: INfseDpsForm = {
  emitente_id: null,
  competencia: new Date().toISOString().slice(0, 10),
  tomador_doc: '',
  tomador_exterior: false,
  tomador_nif: '',
  tomador_c_nao_nif: '',
  tomador_pais: '',
  tomador_cod_postal: '',
  tomador_cidade: '',
  tomador_estado: '',
  tomador_nome: '',
  tomador_email: '',
  tomador_municipio_ibge: '',
  tomador_cep: '',
  tomador_logradouro: '',
  tomador_numero: '',
  tomador_complemento: '',
  tomador_bairro: '',
  c_trib_nac: '',
  c_nbs: '',
  c_ind_op: '',
  c_class_trib: '',
  local_prestacao_ibge: '',
  local_prestacao_exterior: false,
  local_prestacao_pais: '',
  pais_resultado: '',
  com_ext: {
    md_prestacao: '',
    vinc_prest: '0',
    tp_moeda: '',
    v_serv_moeda: '',
    mec_af_comex_p: '01',
    mec_af_comex_t: '01',
    mov_temp_bens: '1',
    n_di: '',
    n_re: '',
    mdic: '0',
  },
  descricao: '',
  v_serv: '',
  trib_issqn: '1',
  tp_ret_issqn: '1',
  p_aliq: '',
  p_tot_trib_fed: '',
  retencoes: { irrf: null, csll: null, pis: null, cofins: null, cp: null },
  deducao_tipo: 'valor',
  deducao: '',
  enviar_email: false,
};
